import { type FC, useMemo } from "react";
import { Card } from "@/components/podkit/Card";
import { Tabs, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";
import { Pill } from "@/components/podkit/status/Pill";
import { Text } from "@/components/podkit/typography/Text";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/Chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import {
    formatCredits,
    type DailyChartPoint,
    type Grouping,
    type TopUser,
} from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";

/** Sort tooltip items: highest value first, "Others" always last. */
const sortTooltipPayload: NonNullable<React.ComponentProps<typeof ChartTooltipContent>["sortPayload"]> = (payload) =>
    [...payload].sort((a, b) => {
        const aIsOthers = a.dataKey === "others";
        const bIsOthers = b.dataKey === "others";
        if (aIsOthers !== bIsOthers) return aIsOthers ? 1 : -1;
        return (Number(b.value) || 0) - (Number(a.value) || 0);
    });

/** Render X-axis day numbers, staggering into two rows on narrow viewports. */
const StaggeredTick: FC<{
    x?: number;
    y?: number;
    index?: number;
    payload?: { value?: string };
    stagger?: boolean;
}> = ({ x = 0, y = 0, index = 0, payload, stagger = false }) => {
    const day = String(payload?.value ?? "").replace(/\D+/g, "");
    const dy = stagger && index % 2 !== 0 ? 24 : 12;
    return (
        <text x={x} y={y} dy={dy} textAnchor="middle" className="fill-content-secondary text-xs">
            {day}
        </text>
    );
};

const USER_COLORS = [
    "rgb(var(--chart-1))",
    "rgb(var(--chart-2))",
    "rgb(var(--chart-3))",
    "rgb(var(--chart-4))",
    "rgb(var(--chart-5))",
    "rgb(var(--chart-6))",
    "rgb(var(--chart-7))",
    "rgb(var(--chart-8))",
    "rgb(var(--chart-9))",
    "rgb(var(--chart-10))",
];

const OTHERS_COLOR = "rgb(var(--chart-others) / 0.8)";
const BRAND_COLOR = USER_COLORS[0];
const ENVIRONMENT_COLOR = USER_COLORS[1];

type Props = {
    chartData: DailyChartPoint[];
    grouping: Grouping;
    onGroupingChange: (g: Grouping) => void;
    topUsers: TopUser[];
    loading: boolean;
    monthLabel: string;
    errorMessage?: string;
};

const GROUPING_OPTIONS = [
    { value: "all" as const, label: "All" },
    { value: "type" as const, label: "By Usage Type" },
    { value: "users" as const, label: "By Users" },
];

export const EnterpriseUsageDailyChart: FC<Props> = ({
    chartData,
    grouping,
    onGroupingChange,
    topUsers,
    loading,
    monthLabel,
    errorMessage,
}) => {
    const { isMobileViewport } = useIsMobileViewport();
    const { isMobileViewport: isNarrowSettingsView } = useIsMobileViewport("(max-width: 1000px)");
    const chartConfig: ChartConfig = useMemo(() => {
        if (grouping === "all") {
            return { total: { label: "Total", color: BRAND_COLOR } };
        }
        if (grouping === "type") {
            return {
                environment: { label: "Environment", color: ENVIRONMENT_COLOR },
                agentic: { label: "Intelligence", color: BRAND_COLOR },
            };
        }
        const cfg: ChartConfig = {};
        topUsers.forEach((u, i) => {
            cfg[u.userId] = { label: u.displayName, color: USER_COLORS[i % USER_COLORS.length] };
        });
        cfg.others = { label: "Others", color: OTHERS_COLOR };
        return cfg;
    }, [grouping, topUsers]);

    const dataKeys = useMemo(() => {
        if (grouping === "all") return ["total"];
        if (grouping === "type") return ["environment", "agentic"];
        return ["others", ...topUsers.map((u) => u.userId).reverse()];
    }, [grouping, topUsers]);

    const colors = useMemo(() => {
        if (grouping === "all") return [BRAND_COLOR];
        if (grouping === "type") return [ENVIRONMENT_COLOR, BRAND_COLOR];
        const userColors = topUsers.map((_, i) => USER_COLORS[i % USER_COLORS.length]);
        return [OTHERS_COLOR, ...userColors.reverse()];
    }, [grouping, topUsers]);

    return (
        <Card className="p-3 sm:p-6">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Text className="whitespace-nowrap text-lg font-semibold text-content-primary">
                        Daily credit usage
                    </Text>
                    <Pill variant="neutral" size="sm">
                        {monthLabel}
                    </Pill>
                </div>
                {/* Override @container (sets container-type:inline-size, collapsing width in flex parents)
                   and force flex-row since we always want horizontal layout here. */}
                <Tabs
                    value={grouping}
                    onValueChange={(v) => onGroupingChange(v as Grouping)}
                    className="w-fit [container-type:normal]"
                >
                    <TabsList className="flex-row">
                        {GROUPING_OPTIONS.map((opt) => (
                            <TabsTrigger
                                key={opt.value}
                                value={opt.value}
                                className="h-7 flex-grow-0 px-3 py-1 text-sm"
                            >
                                {opt.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>

            {loading ? (
                <div className="flex h-[280px] items-center justify-center gap-2" role="status" aria-live="polite">
                    <IconSpinner className="animate-spin" size="base" aria-hidden={true} />
                    <Text>Loading chart data...</Text>
                </div>
            ) : errorMessage || chartData.length === 0 ? (
                <EmptyChartOverlay message={errorMessage ?? `No credit usage for ${monthLabel}`} />
            ) : (
                <>
                    <ChartContainer config={chartConfig} className="h-[280px] w-full">
                        <BarChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                            barCategoryGap="20%"
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="date"
                                tickLine={false}
                                axisLine={false}
                                interval={0}
                                tick={<StaggeredTick stagger={isNarrowSettingsView} />}
                                height={isNarrowSettingsView ? 40 : 24}
                            />
                            {!isMobileViewport && (
                                <YAxis
                                    tickLine={false}
                                    axisLine={false}
                                    tickMargin={8}
                                    tickFormatter={(v: number) => formatCredits(v)}
                                />
                            )}
                            <ChartTooltip
                                cursor={false}
                                content={
                                    <ChartTooltipContent
                                        className="max-w-[280px]"
                                        labelFormatter={(label) => `Credit usage on ${label}`}
                                        valueFormatter={(v) => Math.round(Number(v)).toLocaleString()}
                                        sortPayload={sortTooltipPayload}
                                        showTotal
                                    />
                                }
                            />
                            {dataKeys.map((key, i) => (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    name={(chartConfig[key]?.label as string) ?? key}
                                    stackId="stack"
                                    fill={colors[i]}
                                    maxBarSize={48}
                                    isAnimationActive={false}
                                />
                            ))}
                        </BarChart>
                    </ChartContainer>
                    <Text className="mt-1 text-right text-xs text-content-tertiary">Dates are in UTC</Text>
                    <CustomLegend config={chartConfig} />
                </>
            )}
        </Card>
    );
};

const EmptyChartOverlay: FC<{ message: string }> = ({ message }) => (
    <div className="relative h-[280px]">
        <ChartContainer config={{}} className="h-full w-full opacity-40">
            <BarChart margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
            </BarChart>
        </ChartContainer>
        <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
            <Text className="text-content-secondary">{message}</Text>
        </div>
    </div>
);

const CustomLegend: FC<{ config: ChartConfig }> = ({ config }) => (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 pt-2">
        {Object.entries(config).map(([key, entry]) => (
            <div key={key} className="flex items-center gap-1 whitespace-nowrap text-xs text-content-secondary">
                <div className="size-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: entry.color }} />
                {entry.label}
            </div>
        ))}
    </div>
);
